import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { API_BASE_URL } from '../apiBase';
import { cn } from '../lib/utils';

const GIS_SRC = 'https://accounts.google.com/gsi/client';

let gisLoadPromise = null;

function loadGisScript() {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'));
  if (window.google?.accounts?.id) return Promise.resolve();
  if (gisLoadPromise) return gisLoadPromise;
  gisLoadPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${GIS_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('GIS load failed')));
      if (window.google?.accounts?.id) resolve();
      return;
    }
    const s = document.createElement('script');
    s.src = GIS_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('GIS load failed'));
    document.head.appendChild(s);
  });
  return gisLoadPromise;
}

export async function fetchGoogleAuthConfig() {
  try {
    const { data } = await axios.get(`${API_BASE_URL}/api/public/auth-config`, {
      timeout: 10000,
    });
    const challenge = data?.contextual && data?.google_enabled
      ? await axios.post(API_BASE_URL + '/api/auth/google/challenge', {}, { withCredentials: true, timeout: 10000 })
      : null;
    return {
      contextual: Boolean(data?.contextual),
      nonce: challenge?.data?.nonce || undefined,
      enabled: Boolean(data?.google_enabled && data?.google_client_id),
      clientId: data?.google_client_id || '',
    };
  } catch {
    return { enabled: false, clientId: '' };
  }
}

/**
 * Botão oficial Google Identity Services (id_token → callback).
 * Só renderiza se o escritório tiver GOOGLE_CLIENT_ID configurado.
 */
export default function GoogleSignInButton({
  onCredential,
  text = 'continue_with',
  disabled = false,
  className,
  labelFallback = 'Continuar com Google',
}) {
  const hostId = useId().replace(/:/g, '');
  const hostRef = useRef(null);
  const cbRef = useRef(onCredential);
  const [ready, setReady] = useState(false);
  const [cfg, setCfg] = useState({ enabled: false, clientId: '' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    cbRef.current = onCredential;
  }, [onCredential]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const c = await fetchGoogleAuthConfig();
      if (cancelled) return;
      setCfg(c);
      if (!c.enabled) return;
      try {
        await loadGisScript();
        if (cancelled) return;
        setReady(true);
      } catch {
        if (!cancelled) setError('Não foi possível carregar o Google.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!ready || !cfg.clientId || !hostRef.current || disabled) return;
    const el = hostRef.current;
    el.innerHTML = '';
    try {
      window.google.accounts.id.initialize({
        client_id: cfg.clientId,
        nonce: cfg.nonce,
        callback: async (response) => {
          const cred = response?.credential;
          if (!cred) return;
          setBusy(true);
          setError('');
          try {
            await cbRef.current?.(cred);
          } catch (e) {
            setError(
              e?.response?.data?.detail ||
                e?.message ||
                'Falha ao autenticar com Google.',
            );
          } finally {
            setBusy(false);
          }
        },
        auto_select: false,
        cancel_on_tap_outside: true,
        context: text === 'signup' ? 'signup' : 'signin',
        ux_mode: 'popup',
      });
      let lastWidth;
      const render = () => {
        const width = Math.min(360, Math.floor(el.parentElement?.clientWidth || 320));
        if (width === lastWidth) return;
        lastWidth = width;
        el.innerHTML = '';
        window.google.accounts.id.renderButton(el, {
          theme: 'outline', size: 'large',
          text: text === 'signup' ? 'signup_with' : text === 'signin' ? 'signin_with' : 'continue_with',
          shape: 'pill', width, logo_alignment: 'left',
        });
      };
      render();
      // Classic integration stays one-shot. Only the opted-in auth surface follows width changes.
      const responsive = el.closest('[data-auth-visual="modern"]');
      const observer = responsive && typeof ResizeObserver === 'function' ? new ResizeObserver(() => {
        try { render(); } catch { setError('Google Sign-In indisponível neste navegador.'); }
      }) : null;
      if (el.parentElement) observer?.observe(el.parentElement);
      return () => observer?.disconnect();
    } catch (e) {
      setError('Google Sign-In indisponível neste navegador.');
    }
  }, [ready, cfg.clientId, cfg.nonce, disabled, text]);

  if (!cfg.enabled && !error) return null;

  return (
    <div className={cn('aurax-google-auth', className)} data-testid="google-sign-in">
      {busy ? (
        <div className="aurax-google-auth__busy">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>A validar Google…</span>
        </div>
      ) : null}
      <div
        id={`gis-${hostId}`}
        ref={hostRef}
        className={cn('aurax-google-auth__btn', (disabled || busy) && 'is-disabled')}
        aria-label={labelFallback}
      />
      {cfg.contextual ? <button type="button" disabled={busy || disabled} onClick={async () => {
        setBusy(true);
        setReady(false);
        const fresh = await fetchGoogleAuthConfig();
        setCfg(fresh);
        setReady(fresh.enabled);
        setBusy(false);
        setError(fresh.enabled ? '' : 'Não foi possível iniciar outro login. Recarregue a página.');
      }}>Iniciar novo login Google</button> : null}
      {error ? (
        <p className="aurax-google-auth__error" role="alert">
          {typeof error === 'string' ? error : 'Erro no Google Sign-In'}
        </p>
      ) : null}
    </div>
  );
}

export function AuthDivider({ label = 'ou continue com e-mail' }) {
  return (
    <div className="aurax-auth-divider" role="separator">
      <span>{label}</span>
    </div>
  );
}
